import React from "react";
import {
	IFormFieldProps,
	EWidgets,
	ICustomFormItemProps
} from "@organisms/ZForm/interface";
import { checkCanMultiplePricingPlansFor } from "@/Components/Utils";
import { percentageFormatter } from "@/Utils/Currency";

import { currencyFormatter } from "@/Utils/Currency";
import ZCheckbox from "@atoms/ZCheckbox";
import ZTypography, { FontWeights } from "@atoms/ZTypography";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Divider from "antd/lib/divider";
import { Product, ProductType } from "@/Contracts/Products";
import { handleMarkupMargin } from "@/Components/Utils/PricingCalculator";
import FormBuilder from "antd-form-builder";
import { IProductFormItemProps } from "./interface";
import { EProductDrawerState } from "@/V2Components/pages/Drawers/Products/Product/interface";
import { IOptionProps } from "@atoms/ZSelect/interface";
import { CustomFieldMandatoryFieldDetailsValues } from "@/Contracts/Settings";

export default ({
	isLoading,
	customField,
	customFieldId,
	form,
	onProductTypeChange,
	ProductBundle = null,
	CostPriceInputGroup = [],
	SellPriceInputGroup = [],
	getFieldValues,
	currentProductType = ProductType.Software,
	currentProduct = null,
	getInitialUnitOfMeasure,
	showIndividualProductPricing = false,
	setShowIndividualProductPricing,
	drawerState = EProductDrawerState.ADD,
	isBulkUpdate = false,
	showDynamicPricingSection = false,
	productPricingPlanList = null,
	isBundleEnhancementEnable,
	pricingDisabled,
	selectedPricing
}: IProductFormItemProps): IFormFieldProps | IFormFieldProps[] | null => {
	const type =
		drawerState === EProductDrawerState.BUNDLECHILDEDIT
			? currentProductType
			: form.getFieldValue("type");
	const isBundleProduct = type === ProductType.Bundle;
	const isHardwareProduct = type === ProductType.Hardware;
	const canHaveMultiplePricingPlans = checkCanMultiplePricingPlansFor(
		type ?? ProductType.Software
	);

	if (
		!(currentProduct as Product)?.manufacturer &&
		selectedPricing?.manufacturer
	) {
		form.setFieldsValue({ manufacturer: selectedPricing?.manufacturer });
	}
	const onAddSetupPriceToMarginChange = (e: CheckboxChangeEvent): void => {
		const values = getFieldValues && getFieldValues();
		const { margin, markup } = handleMarkupMargin(
			values?.selected_cost_price,
			values?.sell_price,
			values?.quantity,
			e.target.checked,
			values?.selected_setup_price
		);

		const marginAmount =
			((values?.sell_price ?? 0) - (values?.selected_cost_price ?? 0)) *
				1 +
			(!!e.target.checked ? values?.selected_setup_price ?? 0 : 0);

		form.setFieldsValue({
			margin,
			markup,
			margin_and_amount: {
				margin,
				marginAmount
			}
		});
	};

	const strToCompare = isBulkUpdate ? `item_${customFieldId}` : customFieldId;

	switch (strToCompare) {
		case "item_type": {
			const selectOptions: IOptionProps[] = [
				{ name: "Hardware", value: ProductType.Hardware },
				{ name: "Software", value: ProductType.Software },
				{ name: "Service", value: ProductType.Service },
				{ name: "Labor", value: ProductType.Labor }
			];

			if (!showDynamicPricingSection) {
				selectOptions.push({
					name: "Bundle",
					value: ProductType.Bundle
				});
			}

			const fields: IFormFieldProps[] = [
				{
					key: "type",
					label: "Type",
					hasFeedback: true,
					required: true,
					initialValue: currentProductType ?? ProductType.Software,
					message: "Please select product type",
					widget: EWidgets.SELECT,
					widgetProps: {
						placeholder: "Please select product type",
						onChange: onProductTypeChange,
						selectOptions: selectOptions
					}
				}
			];

			if (type === ProductType.Bundle && ProductBundle) {
				fields.push({
					key: "product_bundle_item",
					wrapperCol: { span: 24 },
					render: ProductBundle
				});
			}
			return fields;
		}
		case "item_description": {
			return {
				key: "description",
				label: "Description",
				widget: EWidgets.INPUTTEXTAREA,
				hasFeedback: true,
				initialValue: currentProduct?.description || "",
				required: isBulkUpdate,
				message: "Please enter product description",
				widgetProps: {
					placeholder: "Please enter product description",
					autoSize: { minRows: 6, maxRows: 6 },
					allowClear: true
				}
			};
		}
		case "item_product_category": {
			return drawerState !== EProductDrawerState.BUNDLECHILDEDIT
				? {
						key: "product_category",
						label: "Category",
						hasFeedback: true,
						widget: EWidgets.INPUT,
						initialValue: (currentProduct as Product)
							?.product_category,
						required: isBulkUpdate,
						message: "Please enter product category",
						widgetProps: {
							width: "full",
							placeholder: "Please enter product category"
						}
				  }
				: [];
		}
		case "item_manufacturer": {
			return !isBundleProduct &&
				drawerState !== EProductDrawerState.BUNDLECHILDEDIT
				? {
						key: "manufacturer",
						label: "Manufacturer",
						hasFeedback: true,
						widget: EWidgets.INPUT,
						initialValue: (currentProduct as Product)?.manufacturer,
						required: isBulkUpdate,
						message: "Please enter manufacturer name",
						widgetProps: {
							width: "full",
							placeholder: "Please enter manufacturer name"
						}
				  }
				: [];
		}
		case "item_manufacturer_part_no": {
			return !isBundleProduct &&
				drawerState !== EProductDrawerState.BUNDLECHILDEDIT
				? {
						key: "manufacturer_part_no",
						label: "Mfg. Number",
						hasFeedback: true,
						widget: EWidgets.INPUT,
						initialValue: (currentProduct as Product)
							?.manufacturer_part_no,
						widgetProps: {
							width: "full",
							placeholder: "Please enter manufacturer part number"
						}
				  }
				: [];
		}
		case "item_should_add_setup_price_to_margin": {
			const setupPriceFields = [
				{
					key: "should_add_setup_price_to_margin",
					wrapperCol: { span: 24 },
					widget: ({ value, onChange }: ICustomFormItemProps) => (
						<div>
							<ZCheckbox
								checked={value}
								onChange={(e: CheckboxChangeEvent) => {
									onChange(e.target.checked);
									onAddSetupPriceToMarginChange(e);
								}}
								disabled={
									isLoading ||
									(isBundleEnhancementEnable &&
										pricingDisabled)
								}
							>
								Add 'Setup Fee' to Margin
							</ZCheckbox>
						</div>
					)
				},
				{
					key: "margin_and_amount",
					wrapperCol: { span: 24 },
					formItemProps: {
						hidden:
							productPricingPlanList &&
							productPricingPlanList?.length > 1
					},
					widget: ({ value }: ICustomFormItemProps) =>
						value ? (
							<div className="rounded bg-gray-3 p-2 mt-n7">
								<ZTypography.ZText weight={FontWeights.MEDIUM}>
									Item Margin:
								</ZTypography.ZText>{" "}
								<ZTypography.ZText>
									{percentageFormatter(value.margin, 100)} (
									{currencyFormatter(value.marginAmount)})
								</ZTypography.ZText>
							</div>
						) : null
				}
			];
			if (
				canHaveMultiplePricingPlans &&
				drawerState !== EProductDrawerState.BUNDLECHILDEDIT &&
				!showDynamicPricingSection
			) {
				return setupPriceFields;
			}

			let fields: IFormFieldProps[] | null = null;
			if (
				isBundleProduct &&
				!isBundleEnhancementEnable &&
				drawerState !== EProductDrawerState.BUNDLECHILDEDIT
			) {
				if (!fields) {
					fields = [];
				}
				fields.push({
					key: "show_individual_product_pricing",
					wrapperCol: { span: 24 },
					widget: ({ value, onChange }: ICustomFormItemProps) => (
						<div className="mt-n2">
							<ZCheckbox
								checked={value}
								onChange={(e: CheckboxChangeEvent) => {
									onChange(e.target.checked);
									setShowIndividualProductPricing &&
										setShowIndividualProductPricing(
											e.target.checked
										);
								}}
								disabled={isLoading}
							>
								Show individual product pricing
							</ZCheckbox>
						</div>
					)
				});
			}
			if (
				showDynamicPricingSection ||
				isHardwareProduct ||
				(isBundleProduct &&
					(!isBundleEnhancementEnable
						? !showIndividualProductPricing
						: true)) ||
				drawerState === EProductDrawerState.BUNDLECHILDEDIT
			) {
				if (!fields) {
					fields = [];
				}
				fields.push(...CostPriceInputGroup);
				fields.push({
					key: "divider",
					wrapperCol: { span: 24 },
					render: () => <Divider className="mb-6" />
				});
				fields.push(...SellPriceInputGroup);
				fields.push(...setupPriceFields);
			}
			return fields
				? {
						key: "accumulation",
						render: () => (
							<div className="bg-gray-3 p-2 pb-0 rounded">
								<FormBuilder
									form={form}
									meta={{
										formItemLayout: [6, 18],
										fields
									}}
								/>
							</div>
						)
				  }
				: null;
		}
		case "item_unit_of_measure": {
			return {
				key: "unit_of_measure",
				label: "UOM",
				hasFeedback: true,
				rules: [
					{
						required:
							customField?.mandatory_field_details?.[
								CustomFieldMandatoryFieldDetailsValues.FOR_UI
							],
						message: "Please select the unit of measure"
					}
				],
				initialValue:
					getInitialUnitOfMeasure && getInitialUnitOfMeasure(),
				widget: EWidgets.SELECT,
				widgetProps: {
					allowClear: true,
					"data-testid": "unit_of_measure_select",
					placeholder: "Please select the unit of measure",
					selectOptions: customField?.allowed_dropdown_values?.map(
						value => ({
							value,
							name: value
						})
					)
				}
			};
		}
		default:
			return null;
	}
};
